import { useCallback, useContext, useState, useEffect } from 'react';
import {
    Alert,
    Box,
    Close,
    Heading,
    Spinner,
    Text,
} from 'theme-ui';
import { Submit } from '~/Forms/Submit';
import { Field } from '~/Forms/Field';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { institutionalOnboardingAccount, getOnboardingStatusAsync } from 'features/auth/authSlice';
import { getProfileOptionsAsync } from 'features/theme/themeSlice'
import { toast } from 'utils/toast';
import { connect, useDispatch } from 'react-redux';
import { Yup } from 'utils/validationRules';
import { InstitutionalOnboardingContext } from '../InstitutionalLanding';

const mapStateToProps = (state) => {
    return { user: state.user, theme: state.theme }
}

const validationSchema = Yup.object().shape({
    title: Yup.string().required('required field'),
    first_name: Yup.string().required('required field'),
    last_name: Yup.string().required('required field'),
    password: Yup.string().required().test('len', 'must be at least 8 characters', val => val !== undefined && val.length >= 8),
    password_confirmation: Yup.string().required('required field').oneOf([Yup.ref('password'), null], 'Passwords must match'),
});

const InstitutionalOnboardingPersonal = ({ user, theme }) => {
    const onboarding = useContext(InstitutionalOnboardingContext);
    const [loading, setLoading] = useState(true);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [profileOptions, setProfileOptions] = useState(theme.profile.options);

    const getOptions = useCallback(() => {
        if (theme.profile.status === 'loading') {
            dispatch(getProfileOptionsAsync());
        }
    }, [theme, dispatch]);

    useEffect(() => {
        getOptions();

        if (theme.profile.status === 'attempted') {
            setProfileOptions(theme.profile.options);
            setLoading(false);
        }

    }, [theme, getOptions]);

    const handleSubmit = (values, actions) => {
        setLoading(true);
        institutionalOnboardingAccount({
            title: values.title,
            first_name: values.first_name,
            last_name: values.last_name,
            password: values.password,
            password_confirmation: values.password_confirmation,
        }).then((response) => {
            toast.success(t('onboarding.institutional.landing.personal.success'), {
                toastId: 'personal-success',
            });
            onboarding.nextStep();
            dispatch(getOnboardingStatusAsync());
            setLoading(false);
        }).catch(({ response }) => {
            response?.data?.errors && actions.setErrors(response.data.errors);
            toast.error(response?.data?.message || t('onboarding.institutional.landing.personal.error'), {
                toastId: 'personal-error',
            });
            actions.setStatus('api_error');
            actions.setSubmitting(false);
            setLoading(false);
        });
    };

    return (
        <>
            {loading && <Spinner variant="styles.spinner" m={'auto'} />}

            <Box sx={{
                display: loading ? 'none' : 'block'
            }}>
                <Formik
                    enableReinitialize
                    initialValues={{
                        title: user?.user?.individual?.title ?? '',
                        first_name: user?.user?.first_name ?? '',
                        last_name: user?.user?.last_name ?? '',
                        email: user?.user?.email ?? '',
                        password: '',
                        password_confirmation: '',
                    }}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ status, setStatus, values, setFieldValue }) => (
                        <Box id="institutionalOnboardingPersonal">
                            { status === 'api_error' ?
                            <Alert role="alert" variant='danger' mb={2}>
                                {t('onboarding.institutional.landing.personal.error')}
                                <Close ml="auto" mr={-2} onClick={() => setStatus(null)} />
                            </Alert> : ''}

                            <Form>
                                <Heading as="h1" variant="h3" sx={{ fontWeight: 500 }} mb={4}>
                                    {t('onboarding.institutional.landing.personal.title')}
                                </Heading>

                                <Text as="p" mb={3} sx={{
                                    fontSize: 2,
                                    color: 'dark'
                                }}>
                                    {t('onboarding.institutional.landing.personal.subtitle')}
                                </Text>

                                {profileOptions.titles.length > 0 &&
                                    <Field
                                        label="Title"
                                        placeholder="Select title"
                                        name="title"
                                        field="searchable-select"
                                        options={profileOptions.titles}
                                        required={true}
                                    />
                                }
                                <Field
                                    label="First name"
                                    name="first_name"
                                    required={true}
                                />
                                <Field
                                    label="Last name"
                                    name="last_name"
                                    required={true}
                                />
                                <Field
                                    label="Email"
                                    name="email"
                                    readOnly="readonly"
                                />

                                <Heading as="h2" variant="form_section" sx={{
                                    fontWeight: 600,
                                    letterSpacing: '-0.01em',
                                    mb: 3,
                                    mt: 4
                                }}>
                                    {t('Create your password')}
                                </Heading>

                                <Field
                                    label="Password"
                                    name="password"
                                    type="password"
                                    groupMb={0}
                                />
                                <Box sx={{ mb: 3, fontSize: '13px', pt: 3, color: 'dark' }}>
                                    <Text as="p">{t('Passwords must include: ')}</Text>
                                    <Text as="p">{t('- At least 8 characters')}</Text>
                                    <Text as="p">{t('- At least one uppercase and one lowercase letter')}</Text>
                                    <Text as="p">{t('- At least one symbol')}</Text>
                                    <Text as="p">{t('- At least one number')}</Text>
                                </Box>
                                <Field
                                    label="Password Confirmation"
                                    name="password_confirmation"
                                    type="password"
                                />

                                <Submit variant="primary" id="personalSubmit" text={t('onboarding.institutional.landing.personal.submit')} sx={{ display: 'block', width: '100%', mt: '40px' }} />
                            </Form>
                        </Box>
                    )}
                </Formik>
            </Box>
        </>
    )
};

export default connect(mapStateToProps, null)(InstitutionalOnboardingPersonal);
